import {createAsyncThunk, createSlice} from '@reduxjs/toolkit'

import {RootState} from './rootReducer'

import {AuthContextInterface} from '../components/AuthProvider'
import {useApi} from '../hooks/useApi'

interface ConsultPdfInfo {
  name: string
  exam: string
  email: string
  consultant: string
  date: string
}

interface ConsultPdfMemberFile {
  question: string
  answer: string
  filename: string
}

interface ConsultPdfMemberSubCatFieldResult {
  field_id: string
  order: number
  question: string
  member: string
  sentiment: string
  bks: string
}

interface ConsultPdfMemberSubCats {
  subCatId: number
  subCatName: string
  fields: ConsultPdfMemberSubCatFieldResult[]
  fieldCount: number
  consultant: string
  allNull: boolean
  atWorst: string
}

interface ConsultPdfMemberOpinionScale {
  question: string
  answer: number
  sentiment: string
}

interface ConsultPdfMemberResult {
  catId: number
  catName: string
  subCats: ConsultPdfMemberSubCats[]
  opScale: Partial<ConsultPdfMemberOpinionScale>
}

interface ConsultPdfMember {
  name: string
  files: ConsultPdfMemberFile[]
  results: ConsultPdfMemberResult[]
  outsides: Record<string, string>
  complete: number
  firmAndRole: string
}

export interface GetConsultPdfResponse {
  info: ConsultPdfInfo
  member: ConsultPdfMember
}

export enum LoadingStatuses {
  Idle,
  Loading,
  Succeeded,
  Failed,
}

type SliceState = {
  pdf: GetConsultPdfResponse | null | undefined
  status: LoadingStatuses
  error?: string | null | undefined
}

type ConsultPdfPayload = {
  auth: AuthContextInterface
  landingId: string
}

export const getConsultPdf = createAsyncThunk<any, ConsultPdfPayload>(
  '/getConsultPdf',
  async ({auth, landingId}) => {
    const endpoint = `/v1/are/admin/consult/pdf/${landingId}`

    return useApi(auth, endpoint, {
      method: 'GET',
    }).then(res => res.json())
  },
)

const initialState: SliceState = {
  pdf: null,
  status: LoadingStatuses.Idle,
  error: undefined,
}

export default createSlice({
  name: 'consult',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getConsultPdf.fulfilled, (state, action) => {
        state.pdf = action.payload
        state.status = LoadingStatuses.Succeeded
        state.error = undefined
      })
      .addCase(getConsultPdf.pending, state => {
        state.pdf = null
        state.status = LoadingStatuses.Loading
        state.error = undefined
      })
      .addCase(getConsultPdf.rejected, state => {
        state.pdf = undefined
        state.status = LoadingStatuses.Failed
        state.error = undefined
      })
  },
})

export const selectConsultPdf = ({consult}: RootState) => consult.pdf
